<template>
  <div class="reset-password-page">
    <div class="reset-password-container">
      <div class="reset-password-card">
        <!-- Tenant Logo -->
        <div class="reset-password-logo">
          <img
            v-if="tenantLogo"
            :src="tenantLogo"
            :alt="tenantName ?? ''"
            class="logo-image"
          />
          <div v-else class="logo-placeholder">
            {{ tenantName || 'Franchise Factory' }}
          </div>
        </div>

        <div class="reset-password-header">
          <h1>{{ t('Reset Password') }}</h1>
          <p class="subtitle">{{ t('Enter the verification code and your new password') }}</p>
        </div>

        <form @submit.prevent="handleResetPassword" class="reset-password-form">
          <!-- Error Message -->
          <div v-if="errorMessage" class="alert alert-error">
            {{ errorMessage }}
          </div>

          <!-- Success Message -->
          <div v-if="successMessage" class="alert alert-success">
            {{ successMessage }}
          </div>

          <!-- Username Field (read-only if from query) -->
          <div class="form-group">
            <label for="username">{{ t('Username') }}</label>
            <input
              id="username"
              v-model="username"
              type="text"
              :placeholder="t('Enter your username')"
              required
              autocomplete="username"
              :disabled="isLoading || resetSuccess"
              data-testid="auth-reset-username"
            />
          </div>

          <!-- Verification Code Field -->
          <div class="form-group">
            <label for="code">{{ t('Verification Code') }}</label>
            <input
              id="code"
              v-model="verificationCode"
              type="text"
              :placeholder="t('Enter verification code')"
              required
              maxlength="6"
              pattern="[0-9]{6}"
              autocomplete="one-time-code"
              :disabled="isLoading || resetSuccess"
              data-testid="auth-reset-code"
            />
            <small class="help-text">{{ t('6-digit code from your email') }}</small>
          </div>

          <!-- New Password Field -->
          <div class="form-group">
            <label for="newPassword">{{ t('New Password') }}</label>
            <input
              id="newPassword"
              v-model="newPassword"
              type="password"
              :placeholder="t('Choose a strong password')"
              required
              autocomplete="new-password"
              minlength="8"
              :disabled="isLoading || resetSuccess"
              data-testid="auth-reset-new-password"
            />
            <small class="help-text">{{ t('Minimum 8 characters') }}</small>
          </div>

          <!-- Confirm Password Field -->
          <div class="form-group">
            <label for="confirmPassword">{{ t('Confirm Password') }}</label>
            <input
              id="confirmPassword"
              v-model="confirmPassword"
              type="password"
              :placeholder="t('Confirm your new password')"
              required
              autocomplete="new-password"
              :disabled="isLoading || resetSuccess"
              data-testid="auth-reset-confirm-password"
            />
          </div>

          <!-- Submit Button -->
          <button
            v-if="!resetSuccess"
            type="submit"
            class="btn btn-primary btn-block"
            :disabled="isLoading"
            data-testid="auth-reset-submit"
          >
            <span v-if="!isLoading">{{ t('Reset Password') }}</span>
            <span v-else>{{ t('Resetting...') }}</span>
          </button>

          <!-- Go to Login Button -->
          <button
            v-else
            type="button"
            class="btn btn-success btn-block"
            @click="goToLogin"
            data-testid="auth-reset-go-to-login"
          >
            {{ t('Continue to Login') }}
          </button>
        </form>

        <!-- Back to Login Link -->
        <div v-if="!resetSuccess" class="reset-password-footer">
          <p>
            {{ t('Remember your password?') }}
            <router-link to="/login" class="login-link">
              {{ t('Back to Login') }}
            </router-link>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import { tenantConfig } from '@/services/tenantConfigService'
import { log } from '@/utils/DebugLogger'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { resetPassword } = useAuth()

// Tenant branding
const tenantLogo = computed<string | null>(() => tenantConfig.get<string>('logo_url'))
const tenantName = computed<string | null>(() => tenantConfig.get<string>('tenant_name'))
const primaryColor = computed<string>(() => tenantConfig.get<string>('primary_color') || '#667eea')
const secondaryColor = computed<string>(() => tenantConfig.get<string>('secondary_color') || '#764ba2')

// Form state
const username = ref<string>('')
const verificationCode = ref<string>('')
const newPassword = ref<string>('')
const confirmPassword = ref<string>('')
const isLoading = ref<boolean>(false)
const errorMessage = ref<string>('')
const successMessage = ref<string>('')
const resetSuccess = ref<boolean>(false)

onMounted(() => {
  // Pre-fill username from query params if available
  const queryUsername = route.query.username
  if (typeof queryUsername === 'string') {
    username.value = queryUsername
  }

  log('ResetPasswordPage', 'onMounted', 'Reset password page loaded', {
    username: username.value
  })
})

/**
 * Handle reset password form submission
 */
async function handleResetPassword(): Promise<void> {
  try {
    isLoading.value = true
    errorMessage.value = ''
    successMessage.value = ''

    // Validate password match
    if (newPassword.value !== confirmPassword.value) {
      errorMessage.value = t('Passwords do not match')
      isLoading.value = false
      return
    }

    log('ResetPasswordPage', 'handleResetPassword', 'Attempting password reset', {
      username: username.value,
      code: verificationCode.value
    })

    const result = await resetPassword(
      username.value,
      verificationCode.value,
      newPassword.value
    )

    if (result.success) {
      log('ResetPasswordPage', 'handleResetPassword', '✅ Password reset successful')

      resetSuccess.value = true
      successMessage.value = t('Password reset successfully! You can now log in with your new password.')
    } else {
      log('ResetPasswordPage', 'handleResetPassword', '❌ Password reset failed:', result.error)
      errorMessage.value = result.error || t('Failed to reset password')
    }
  } catch (error) {
    log('ResetPasswordPage', 'handleResetPassword', '❌ Password reset error:', error)
    errorMessage.value = t('An error occurred. Please try again.')
  } finally {
    isLoading.value = false
  }
}

/**
 * Navigate to login page
 */
function goToLogin(): void {
  router.push('/login')
}
</script>

<style scoped>
.reset-password-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: v-bind(primaryColor);
  padding: 1rem;
}

.reset-password-container {
  width: 100%;
  max-width: 480px;
}

.reset-password-card {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
}

.reset-password-logo {
  text-align: center;
  margin-bottom: 1.5rem;
}

.logo-image {
  max-width: 180px;
  max-height: 80px;
  height: auto;
  object-fit: contain;
}

.logo-placeholder {
  font-size: 1.5rem;
  font-weight: 700;
  color: v-bind(primaryColor);
  padding: 1rem;
}

.reset-password-header {
  text-align: center;
  margin-bottom: 2rem;
}

.reset-password-header h1 {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.subtitle {
  color: #718096;
  font-size: 0.875rem;
}

.reset-password-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 500;
  color: #2d3748;
  font-size: 0.875rem;
}

.form-group input {
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: v-bind(primaryColor);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-group input:disabled {
  background-color: #f7fafc;
  cursor: not-allowed;
}

.help-text {
  color: #718096;
  font-size: 0.75rem;
  margin-top: -0.25rem;
}

.btn {
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: v-bind(secondaryColor);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-success {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
}

.btn-success:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-block {
  width: 100%;
}

.alert {
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
}

.alert-error {
  background-color: #fed7d7;
  color: #c53030;
  border: 1px solid #fc8181;
}

.alert-success {
  background-color: #c6f6d5;
  color: #22543d;
  border: 1px solid #68d391;
}

.reset-password-footer {
  margin-top: 1.5rem;
  text-align: center;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.reset-password-footer p {
  color: #718096;
  font-size: 0.875rem;
}

.login-link {
  color: v-bind(primaryColor);
  font-weight: 500;
  text-decoration: none;
  transition: color 0.2s;
}

.login-link:hover {
  color: v-bind(secondaryColor);
  text-decoration: underline;
}
</style>
